import MissingResourcesAlert from '@/shared/components/MissingResourcesAlert'
import { Table, TableColumnHeader, TableRow } from '@/common'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import useI18n from '@/shared/hooks/usei18n'
import SupplierRow from './SupplierRow'
import { useGetAllImportedGoods } from '@/features/cbam-reporting/hooks/importedGoods'
import { useGetAllInstallationsForOrganization } from '@/features/suppliers/hooks/installations'
import { LoadingScreen } from '@/shared/components'
import { useMemo, useState } from 'react'
import { groupBy } from 'es-toolkit'
import DeletePartnershipDialog from '../dialogs/DeletePartnershipDialog'
import useDialog from '@/common/hooks/useDialog'

export default function SuppliersList({
  suppliers,
  organizationId,
  missingResourcesAction
}: {
  suppliers: Partnership[]
  organizationId: string
  missingResourcesAction: {
    label: string
    onClick: () => void
  }
}) {
  const { t } = useI18n()
  const importedGoodsQuery = useGetAllImportedGoods(organizationId)
  const installationsQuery = useGetAllInstallationsForOrganization(organizationId)
  const {
    open: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog
  } = useDialog()
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null)

  const isLoadingImportedGoods = importedGoodsQuery.isLoading
  const isLoadingInstallations = installationsQuery.isLoading
  const importedGoods = importedGoodsQuery.data || []

  const installationsByPartnership = useMemo(() => {
    const installations = installationsQuery.data || []
    return groupBy(installations, installation => installation.partnershipId)
  }, [installationsQuery.data])

  const handleDeleteClick = (supplier: Partnership) => {
    setSelectedPartnership(supplier)
    openDeleteDialog()
  }

  if (isLoadingImportedGoods || isLoadingInstallations) {
    return <LoadingScreen />
  }

  return (
    <>
      {suppliers.length === 0 ? (
        <MissingResourcesAlert
          title={t('suppliers:suppliersPage.suppliersList.noSuppliers.title')}
          description={t('suppliers:suppliersPage.suppliersList.noSuppliers.description')}
          action={{
            label: missingResourcesAction.label,
            onClick: missingResourcesAction.onClick
          }}
        />
      ) : (
        <div className="overflow-x-auto w-full">
          <Table className="table-fixed">
            <thead>
              <TableRow>
                <TableColumnHeader className="w-48">
                  {t('suppliers:suppliersPage.suppliersList.supplierTable.headers.supplierName')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36">
                  {t('suppliers:suppliersPage.suppliersList.supplierTable.headers.location')}
                </TableColumnHeader>
                <TableColumnHeader align="right" className="w-36">
                  {t(
                    'suppliers:suppliersPage.suppliersList.supplierTable.headers.numberOfInstallations'
                  )}
                </TableColumnHeader>
                <TableColumnHeader align="right" className="w-36">
                  {t('suppliers:suppliersPage.suppliersList.supplierTable.headers.quantityReported')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36" />
              </TableRow>
            </thead>
            <tbody>
              {suppliers.map(supplier => (
                <SupplierRow
                  key={supplier.id}
                  supplier={supplier}
                  importedGoods={importedGoods.filter(
                    importedGood => importedGood.partnershipId === supplier.id
                  )}
                  installations={installationsByPartnership[supplier.id] || []}
                  onDeleteClick={() => handleDeleteClick(supplier)}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {selectedPartnership && (
        <DeletePartnershipDialog
          isOpen={isDeleteDialogOpen}
          close={closeDeleteDialog}
          partnershipName={selectedPartnership.name}
          partnershipId={selectedPartnership.id}
        />
      )}
    </>
  )
}
